/** Store-bound form controls for the settings / debug sheets. Each exposes `sync()` to pull from the store. */
import { button, h } from './dom';

export interface Control {
  el: HTMLElement;
  sync(): void;
}

let uid = 0;
export function nextId(prefix: string): string {
  uid += 1;
  return `${prefix}-${uid}`;
}

export function section(title: string, children: (Node | null)[], opts?: { value?: HTMLElement; help?: string }): HTMLElement {
  const titleId = nextId('uz-sec');
  const head = h('div', 'uz-sec-head', null, [h('h3', 'uz-sec-title', { id: titleId }, [title]), opts?.value ?? null]);
  const sec = h('section', 'uz-sec', { 'aria-labelledby': titleId }, [head]);
  if (opts?.help) sec.append(h('p', 'uz-help', null, [opts.help]));
  for (const c of children) if (c) sec.append(c);
  return sec;
}

export interface SegOption<T extends string> {
  value: T;
  label: string;
  sub?: string;
}

/** Group of toggle buttons (aria-pressed). */
export function segmented<T extends string>(opts: {
  label: string;
  options: SegOption<T>[];
  get: () => T;
  set: (v: T) => void;
  columns?: number;
}): Control {
  const el = h('div', 'uz-seg', { role: 'group', 'aria-label': opts.label });
  el.style.setProperty('--cols', String(opts.columns ?? opts.options.length));
  const buttons = opts.options.map((o) => {
    const b = button('uz-opt', [o.label, o.sub ? h('small', null, null, [o.sub]) : null], () => opts.set(o.value));
    b.dataset.value = o.value;
    b.setAttribute('aria-pressed', 'false');
    el.append(b);
    return b;
  });
  const sync = () => {
    const v = opts.get();
    for (const b of buttons) b.setAttribute('aria-pressed', String(b.dataset.value === v));
  };
  sync();
  return { el, sync };
}

/** Range slider with a formatted value shown in the section header. */
export function slider(opts: {
  label: string;
  min: number;
  max: number;
  step: number;
  get: () => number;
  set: (v: number) => void;
  format: (v: number) => string;
  scale?: [string, string];
}): Control & { valueEl: HTMLElement; input: HTMLInputElement } {
  const id = nextId('uz-range');
  const input = h('input', 'uz-range', {
    id,
    type: 'range',
    min: opts.min,
    max: opts.max,
    step: opts.step,
    'aria-label': opts.label,
  });
  const valueEl = h('span', 'uz-sec-value', { 'aria-hidden': 'true' });
  const paint = (v: number) => {
    const f = ((v - opts.min) / (opts.max - opts.min)) * 100;
    input.style.setProperty('--fill', `${Math.min(100, Math.max(0, f)).toFixed(1)}%`);
    const text = opts.format(v);
    valueEl.textContent = text;
    input.setAttribute('aria-valuetext', text);
  };
  input.addEventListener('input', () => {
    const v = Number(input.value);
    paint(v);
    opts.set(v);
  });
  const children: Node[] = [input];
  if (opts.scale) {
    children.push(h('div', 'uz-range-scale', { 'aria-hidden': 'true' }, [h('span', null, null, [opts.scale[0]]), h('span', null, null, [opts.scale[1]])]));
  }
  const el = h('div', 'uz-range-wrap', null, children);
  const sync = () => {
    const v = opts.get();
    if (document.activeElement !== input || Number(input.value) !== v) input.value = String(v);
    paint(v);
  };
  sync();
  return { el, sync, valueEl, input };
}

/** On/off switch row. */
export function toggle(opts: { label: string; sub?: string; get: () => boolean; set: (v: boolean) => void }): Control & { button: HTMLButtonElement } {
  const labelId = nextId('uz-sw');
  const sw = button('uz-switch', [], () => opts.set(!opts.get()), { role: 'switch', 'aria-labelledby': labelId, 'aria-checked': 'false' });
  const label = h('span', 'uz-row-label', { id: labelId }, [opts.label, opts.sub ? h('small', null, null, [opts.sub]) : null]);
  label.addEventListener('click', () => sw.click());
  const el = h('div', 'uz-row', null, [label, sw]);
  const sync = () => sw.setAttribute('aria-checked', String(opts.get()));
  sync();
  return { el, sync, button: sw };
}
